import { useState } from "react";
import * as Y from "yjs";
import { initialsOf, isAssistantId, newId } from "@worktools/shared";
import { useBlockProps, useSharedProp } from "../../sync/hooks";
import { useBoard } from "../../sync/BoardContext";
import { IconTrash } from "../icons";

type Item = {
  id: string;
  text: string;
  done: boolean;
  doneBy?: string;
  doneByName?: string;
  doneByAvatar?: string | null;
};

export function ChecklistBlock({ blockId }: { blockId: string }) {
  const { me } = useBoard();
  const props = useBlockProps<{ items: Item[] }>(blockId);
  const items = useSharedProp<Y.Array<Y.Map<any>>>(blockId, "items");
  const [draft, setDraft] = useState("");

  const list = props.items ?? [];
  const done = list.filter((item) => item.done).length;

  const add = () => {
    const text = draft.trim();
    if (!text || !items) return;
    const entry = new Y.Map<any>();
    entry.set("id", newId("it"));
    entry.set("text", text);
    entry.set("done", false);
    items.push([entry]);
    setDraft("");
  };

  const toggle = (index: number) => {
    const entry = items?.get(index);
    if (!entry) return;
    const next = !entry.get("done");
    entry.doc?.transact(() => {
      entry.set("done", next);
      entry.set("doneBy", next ? me.id : undefined);
      entry.set("doneByName", next ? me.name : undefined);
      entry.set("doneByAvatar", next ? me.avatarUrl : undefined);
    });
  };

  const rename = (index: number, text: string) => items?.get(index)?.set("text", text);
  const remove = (index: number) => items?.delete(index, 1);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1, height: 4, borderRadius: 99, background: "var(--paper-sunken)" }}>
          <div
            style={{
              width: `${list.length ? (done / list.length) * 100 : 0}%`,
              height: "100%",
              borderRadius: 99,
              background: "var(--accent)",
              transition: "width .2s",
            }}
          />
        </div>
        <span className="label">
          {done}/{list.length}
        </span>
      </div>

      <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 2 }} className="scroll">
        {list.map((item, index) => (
          <div key={item.id} className="check-row">
            <button className="check-box" onClick={() => toggle(index)} aria-pressed={item.done}>
              {item.done && (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2">
                  <path d="M4 12.5l5.5 5.5L20 6.5" />
                </svg>
              )}
            </button>
            <input
              value={item.text}
              onChange={(event) => rename(index, event.target.value)}
              style={{
                flex: 1,
                border: "none",
                background: "none",
                padding: "3px 0",
                textDecoration: item.done ? "line-through" : "none",
                color: item.done ? "var(--ink-faint)" : "var(--ink)",
              }}
            />
            {item.doneByName && (
              <span className="who" title={`Abgehakt von ${item.doneByName}`}>
                {item.doneBy && isAssistantId(item.doneBy) ? (
                  <img src="/claude-logo.svg" alt="Claude" />
                ) : item.doneByAvatar ? (
                  <img src={item.doneByAvatar} alt={item.doneByName} />
                ) : (
                  <span className="who__fallback">{initialsOf(item.doneByName)}</span>
                )}
              </span>
            )}
            <button className="row-action" onClick={() => remove(index)} title="Entfernen">
              <IconTrash size={13} />
            </button>
          </div>
        ))}
      </div>

      <input
        type="text"
        value={draft}
        placeholder="Punkt hinzufügen…"
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => event.key === "Enter" && add()}
      />
    </div>
  );
}
